import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  HomeBasesSection, FlightsSection, LoyaltySection, LodgingSection,
  BudgetSection, Act60Section, HardRulesSection, OptimizationSection,
} from "../components/profile/sections";
import {
  IdentitySection, DocumentsSection, CompanionsSection, HealthSection, EntitiesSection,
} from "../components/profile/sections2";
import AccountSection from "../components/profile/AccountSection";
import SensitiveReveal from "../components/profile/SensitiveReveal";

const SECTIONS = [
  { key: "identity", label: "Identity", Comp: IdentitySection },
  { key: "documents", label: "Travel documents", Comp: DocumentsSection },
  { key: "home_bases", label: "Home bases & airports", Comp: HomeBasesSection },
  { key: "flights", label: "Flight preferences", Comp: FlightsSection },
  { key: "lodging", label: "Hotels & lodging", Comp: LodgingSection },
  { key: "loyalty", label: "Loyalty & memberships", Comp: LoyaltySection },
  { key: "companions", label: "Companions & recurring", Comp: CompanionsSection },
  { key: "health", label: "Health & diving", Comp: HealthSection },
  { key: "entities", label: "Entities & attribution", Comp: EntitiesSection },
  { key: "budget", label: "Budget & points", Comp: BudgetSection },
  { key: "act60", label: "Act 60 residency", Comp: Act60Section },
  { key: "hard_rules", label: "Hard rules", Comp: HardRulesSection },
  { key: "optimization", label: "Optimization", Comp: OptimizationSection },
];

// Mirror of backend pii.TIER1_SPEC — used to overlay revealed plaintext into local state.
const TIER1 = {
  identity: { scalars: ["date_of_birth"], lists: { emergency_contacts: ["name", "phone"] } },
  documents: { scalars: ["known_traveler_number", "redress_number", "drivers_license_number"], lists: { passports: ["number"] } },
  companions: { scalars: [], lists: { companions: ["date_of_birth", "known_traveler_number", "redress_number", "passport_number"] } },
  health: { scalars: ["medical_notes", "dive_cert_number", "dan_insurance_number"], lists: {} },
};

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [savingKey, setSavingKey] = useState(null);

  const load = () =>
    api.get("/profile")
      .then(({ data }) => setProfile(data))
      .catch(() => toast.error("Could not load your travel profile"));

  useEffect(() => { load(); }, []);

  const patchSection = (key, patch) =>
    setProfile((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const saveSection = async (key) => {
    setSavingKey(key);
    try {
      const { data } = await api.put("/profile", { [key]: profile[key] });
      setProfile(data);
      toast.success("Section saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingKey(null);
    }
  };

  const applyReveal = (revealed) =>
    setProfile((p) => {
      const next = { ...p };
      for (const [sk, spec] of Object.entries(TIER1)) {
        const r = revealed[sk];
        if (!r || !next[sk]) continue;
        const sec = { ...next[sk] };
        spec.scalars.forEach((leaf) => { if (r[leaf]) sec[leaf] = r[leaf]; });
        for (const [lk, leaves] of Object.entries(spec.lists)) {
          sec[lk] = (sec[lk] || []).map((row, i) => {
            const rr = (r[lk] || [])[i];
            if (!rr) return row;
            const nrow = { ...row };
            leaves.forEach((leaf) => { if (rr[leaf]) nrow[leaf] = rr[leaf]; });
            return nrow;
          });
        }
        next[sk] = sec;
      }
      return next;
    });

  return (
    <div>
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 py-10 md:py-16" data-testid="profile-page">
        <div className="mb-10">
          <p className="label-tag mb-3">Travel Profile</p>
          <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl mb-2">How you travel.</h1>
          <p className="text-sand-600 text-base mt-3 max-w-2xl">
            Identity, documents, and preferences in one place. Search defaults feed Duffel,
            soft weights rank options, hard rules filter them out — the AI planner reads all of it.
          </p>
          <div className="divider-strong mt-4" />
        </div>

        {profile === null ? (
          <div className="space-y-6">
            {[0, 1, 2].map((i) => <div key={i} className="rounded-card border border-hairline bg-surface h-52 animate-pulse" />)}
          </div>
        ) : (
          <>
            <SensitiveReveal onReveal={applyReveal} onDeleted={load} />
            <div className="flex gap-10">
              <aside className="hidden lg:block w-56 shrink-0">
                <nav className="sticky top-24 space-y-1" data-testid="profile-section-nav">
                  {SECTIONS.map((s) => (
                    <a key={s.key} href={`#${s.key}`}
                      className="block px-3 py-2 text-sm text-sand-600 hover:text-ember hover:bg-sand-50 border-l-2 border-transparent hover:border-ember transition-colors"
                      data-testid={`profile-nav-${s.key}`}>
                      {s.label}
                    </a>
                  ))}
                  <a href="#account"
                    className="block px-3 py-2 text-sm text-sand-600 hover:text-ember hover:bg-sand-50 border-l-2 border-transparent hover:border-ember transition-colors"
                    data-testid="profile-nav-account">
                    Account &amp; data
                  </a>
                </nav>
              </aside>
              <div className="flex-1 min-w-0 space-y-8">
                {SECTIONS.map(({ key, Comp }) => (
                  <Comp key={key} data={profile[key]} pii={profile.pii_meta} patch={(p) => patchSection(key, p)}
                    onSave={() => saveSection(key)} saving={savingKey === key} />
                ))}
                <AccountSection />
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
